import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import TourEditForm, {TourFormValues} from "./TourEditForm";
import {updateTour} from "../../../api/tourApi";
import {Tour} from "../../../models/tour";
import {MessageType} from "../../components/MessageBox";
import {localInputToUtc, utcToLocalInput} from "../../../utils/formatDateTime";
import {UserRole} from "../../../models/account";

interface Props {
    tour: Tour;
    role: UserRole;
    setMessage: (msg: MessageType) => void;
    onSaved: () => void;
}

export default function TourUpdateForm({tour, role, setMessage, onSaved}: Props) {
    const {t} = useTranslation();

    const [values, setValues] = useState<TourFormValues>({
        destination: "",
        dateFrom: "",
        timeFrom: "",
        dateTo: "",
        timeTo: ""
    });

    const adminRights = role === UserRole.ADMIN || role === UserRole.CREATOR;

    useEffect(() => {
        const from = utcToLocalInput(tour.validFrom); // YYYY-MM-DDTHH:mm
        const to = utcToLocalInput(tour.validTo);

        setValues({
            destination: tour.destination ?? "",
            dateFrom: from.slice(0, 10),
            timeFrom: from.slice(11, 16),
            dateTo: to.slice(0, 10),
            timeTo: to.slice(11, 16)
        });
    }, [tour]);

    const handleChange = (field: keyof TourFormValues, value: string) => {
        setValues(prev => ({...prev, [field]: value}));
    };

    const isValidRange =
        values.dateFrom &&
        values.timeFrom &&
        values.dateTo &&
        values.timeTo &&
        new Date(`${values.dateTo}T${values.timeTo}`) >=
        new Date(`${values.dateFrom}T${values.timeFrom}`);

    const disableSave = !adminRights || !isValidRange;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setMessage(null);

        if (!isValidRange) {
            setMessage({type: "error", text: t("tours.messages.invalidDateRange")});
            return;
        }

        try {
            const dto = {
                destination: values.destination,
                validFrom: localInputToUtc(`${values.dateFrom}T${values.timeFrom}`),
                validTo: localInputToUtc(`${values.dateTo}T${values.timeTo}`)
            };

            await updateTour(tour.id, dto);

            setMessage({type: "success", text: t("common.messages.success")});
            onSaved();
        } catch {
            setMessage({type: "error", text: t("common.messages.error")});
        }
    };

    return (
        <form onSubmit={handleSubmit} className="form-container">
            <TourEditForm values={values} disableFields={!adminRights} onChange={handleChange}/>

            {!isValidRange && (
                <p className="form-error">{t("tours.messages.invalidDateRange")}</p>
            )}

            {adminRights &&
                <button type="submit" className="form-button" disabled={!isValidRange}>
                    {t("common.button.save")}
                </button>
            }

        </form>
    );
}
